/**
 * R11 G72 — the Data surfaces' API: Data › Model (#662), Sources (#663), Quality (#664) and an app's
 * Import preview (#665). Read hooks over GET /api/data/*, GET /api/quality/findings and
 * POST /api/data-tab/import/preview (api/src/data-tab/routes.ts).
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export interface DataSource {
  id: string;
  kind: 'connector' | 'forge' | 'import_profile' | 'core_data';
  code: string;
  label: string;
  feeds: string[];
  apps: string[];
  schedule: string | null;
  lastSync: string | null;
  lastOutcome: string | null;
  owner: string | null;
  classification: string | null;
  environment: string | null;
  active: boolean;
}

export interface ModelEntityType {
  code: string;
  label: string;
  plural: string | null;
  description: string | null;
  recordCount: number;
  apps: string[];
  fields: { code: string; label: string; dataType: string; inherited: boolean }[];
  relationships: { code: string; label: string; direction: 'out' | 'in'; otherTypes: string[] }[];
  workflows: { code: string; label: string; states: string[] }[];
  checks: { code: string; label: string }[];
  behaviours: string[];
}
export interface ModelPrimitive {
  code: string;
  label: string;
  description: string | null;
  behaviours: string[];
  entityTypes: ModelEntityType[];
}
export interface ModelInterrogative {
  code: string;
  label: string;
  description: string | null;
  primitives: ModelPrimitive[];
}
export interface DataModel {
  app: string | null;
  interrogatives: ModelInterrogative[];
}

export interface ImportMapping {
  entityType?: string;
  nameColumn: string | null;
  keyColumn: string | null;
  columns: Record<string, string>;
}
export interface ImportPreviewRow {
  line: number;
  key: string | null;
  name: string;
  action: 'create' | 'update' | 'unchanged' | 'skip';
  reasons: string[];
  warnings: string[];
  attrs: Record<string, string>;
}
export interface ImportPreview {
  app: string;
  entityType: string;
  dryRun: true;
  headers: string[];
  fields: { code: string; label: string; dataType: string }[];
  mapping: ImportMapping & { entityType: string };
  unmapped: string[];
  headerErrors: string[];
  counts: { total: number; created: number; updated: number; unchanged: number; skipped: number };
  rows: ImportPreviewRow[];
}

const q = (app?: string | null) => (app ? `?app=${encodeURIComponent(app)}` : '');

export function useDataSources(app?: string | null) {
  return useQuery<{ sources: DataSource[] }>({ queryKey: ['cigs', 'data', 'sources', app ?? null], queryFn: () => apiFetch(`/data/sources${q(app)}`) });
}

export function useDataModel(app?: string | null) {
  return useQuery<DataModel>({ queryKey: ['cigs', 'data', 'model', app ?? null], queryFn: () => apiFetch(`/data/model${q(app)}`), staleTime: 60_000 });
}

export function previewImport(body: { app: string; entityType: string; csv: string; mapping?: ImportMapping }): Promise<ImportPreview> {
  return apiFetch<ImportPreview>('/data-tab/import/preview', { method: 'POST', body });
}
